import { AxiosRequestConfig } from 'axios';

import request from 'src/services/request';
import { ICompany } from 'src/models';

type companyPathArgs = {
  companyId?: string | number;
};

type uploadFileArgs = companyPathArgs & { file: FormData };

export function getCompany({ companyId }: companyPathArgs, config?: AxiosRequestConfig) {
  return request.get<void, ICompany>(`/companies/${companyId}`, config);
}

export function updateCompany({ companyId, ...payload }, config?: AxiosRequestConfig) {
  return request.put<void, ICompany>(`/companies/${companyId}`, payload, config);
}
export function resetCandidateTest({ companyId, jobId, applicantId, testId }, config?: AxiosRequestConfig) {
  return request.put<void>(
    `companies/${companyId}/jobs/${jobId}/applicants/${applicantId}/tests/${testId}/reset`,
    {},
    config
  );
}

export function searchCandidates(companySlug: string | number, params: { q: string; page: number }) {
  return request.get<void, any>(`/companies/${companySlug}/search_candidates`, { params });
}
export function fuzzySearchCandidates(params: { q: string; page: number }) {
  return request.get<void, any>(`/companies/search_candidates`, { params });
}

export function getCompanyInterests({ companyId }: companyPathArgs, config?: AxiosRequestConfig) {
  return request.get<void, any>(`/companies/${companyId}/new_interests`, config);
}

export function getCompanyActiveJobsCount({ companyId }: companyPathArgs, config?: AxiosRequestConfig) {
  return request.get<void, any>(`/companies/${companyId}/jobs/active/count`, config);
}

export function getCompanyAccessPolicies({ companyId }: companyPathArgs, config?: AxiosRequestConfig) {
  return request.get(`/companies/${companyId}/policies`, config);
}

type inviteMembersArgs = {
  companySlug: string | number;
  job_id: number;
  invite: { email: string; permission: string };
};
export function inviteMembers({ companySlug, ...payload }: inviteMembersArgs, config?: AxiosRequestConfig) {
  return request.post<void, any>(`/companies/${companySlug}/invites`, payload, config);
}

type ImageType = 'cover_picture' | 'logo' | 'desc_image' | 'influencer_avatar' | 'influencer_image' | 'team_desc_image';
/* FormData values: 
    image_type: ImageType
    upl_file: File
*/
export function uploadImage({ companyId, file }: uploadFileArgs, config?: AxiosRequestConfig) {
  return request.put(`/companies/${companyId}/upload_image`, file, config);
}

type removeImageArgs = { companyId: string | number; image_type: ImageType };
export function removeImage({ companyId, ...payload }: removeImageArgs, config?: AxiosRequestConfig) {
  return request.put(`/companies/${companyId}/destroy_image`, payload, config);
}
export function updateDescription({ companyId, ...payload }, config?: AxiosRequestConfig) {
  return request.put(`/companies/${companyId}/description`, payload, config);
}
export function updateInfluencer({ companyId, ...payload }, config?: AxiosRequestConfig) {
  return request.put(`/companies/${companyId}/influencer`, payload, config);
}
export function uploadMemberImage({ companyId, file }: uploadFileArgs, config?: AxiosRequestConfig) {
  return request.put(`/companies/${companyId}/upload_member_image`, file, config);
}
export function updateTeam({ companyId, ...payload }, config?: AxiosRequestConfig) {
  return request.put(`/companies/${companyId}/update_team`, payload, config);
}
export function updateHeadquarters({ companyId, ...payload }, config?: AxiosRequestConfig) {
  return request.put(`/companies/${companyId}/update_headquarters`, payload, config);
}
export function updateFreeTexts({ companyId, ...payload }, config?: AxiosRequestConfig) {
  return request.put(`/companies/${companyId}/update_free_texts`, payload, config);
}
export function updateMedia({ companyId, ...payload }, config?: AxiosRequestConfig) {
  return request.put(`/companies/${companyId}/update_media`, payload, config);
}
export function uploadMediaImage({ companyId, file }: uploadFileArgs, config?: AxiosRequestConfig) {
  return request.put(`/companies/${companyId}/upload_media_desc_image`, file, config);
}
export function updateOffices({ companyId, ...payload }, config?: AxiosRequestConfig) {
  return request.put(`/companies/${companyId}/update_offices`, payload, config);
}
export function updateSettings({ companyId, ...payload }, config?: AxiosRequestConfig) {
  return request.put(`/companies/${companyId}/update_settings`, payload, config);
}
export function uploadDisclaimer({ companyId, file }: uploadFileArgs, config?: AxiosRequestConfig) {
  return request.put<void, { file_url: string; filename: string }>(
    `/companies/${companyId}/upload_disclaimer`,
    file,
    config
  );
}
export function getActivities({ companyId }, config?: AxiosRequestConfig) {
  return request.get(`/companies/${companyId}/activities`, config);
}

type createActivityArgs = companyPathArgs & {
  name: string;
  industry: string;
  desc_type: string;
  desc_text: string;
  desc_video?: string;
};
export function createActivity({ companyId, ...payload }: createActivityArgs, config?: AxiosRequestConfig) {
  return request.post(`/companies/${companyId}/activities`, payload, config);
}

type updateActivityArgs = createActivityArgs & { activityId: number; remove_desc_image?: boolean };
export function updateActivity({ companyId, activityId, ...payload }: updateActivityArgs, config?: AxiosRequestConfig) {
  return request.put(`/companies/${companyId}/activities/${activityId}`, payload, config);
}

type deleteActivityArgs = companyPathArgs & { activityId: string | number };
export function deleteActivity({ companyId, activityId }: deleteActivityArgs, config?: AxiosRequestConfig) {
  return request.delete(`/companies/${companyId}/activities/${activityId}`, config);
}

export function uploadActivityImage({ companyId, file }: uploadFileArgs, config?: AxiosRequestConfig) {
  return request.put(`/companies/${companyId}/activities/upload_image`, file, config);
}

// CompanyPreferences

// EmailTemplates
const baseEmailTemplatesURL = '/companies/companyId/email_templates';
export const EmailTemplatesAPI = {
  getCompanyEmailTemplates: function(qs) {
    return `${baseEmailTemplatesURL}${qs}`;
  },
  getCompanyEmailTemplate: function(templateId) {
    return `${baseEmailTemplatesURL}/${templateId}`;
  },
  deleteCompanyEmailTemplate: function(templateId) {
    return `${baseEmailTemplatesURL}/${templateId}`;
  },
  createCompanyEmailTemplate: function() {
    return `${baseEmailTemplatesURL}`;
  },
  updateCompanyEmailTemplate: function(id) {
    return `${baseEmailTemplatesURL}/${id}`;
  }
};

// Hiring teams entity
function getTeamMembers(companySlug: string | number, qs, config?: AxiosRequestConfig) {
  return request.get(`/companies/${companySlug}/preferences/hiring_teams${qs}`, config);
}
function activateStatus(companySlug: string | number, payload: Array<number>) {
  return request.put<void, any>(`/companies/${companySlug}/preferences/hiring_teams/activate`, { user_ids: payload });
}
function deactivateStatus(companySlug: string | number, payload: Array<number>) {
  return request.delete<void, any>(`/companies/${companySlug}/preferences/hiring_teams`, {
    data: { user_ids: payload }
  });
}
function cancelInvites(companySlug: string | number, payload: Array<number>) {
  return request.delete<void, any>(`/companies/${companySlug}/preferences/hiring_teams`, {
    data: { invite_ids: payload }
  });
}
function changeRole({ companySlug, userId, permission }) {
  return request.put<void, any>(`/companies/${companySlug}/preferences/hiring_teams`, {
    user_id: userId,
    permission
  });
}
function resendInvitations({ companySlug, ids }) {
  return request.put<void, any>(`/companies/${companySlug}/preferences/hiring_teams/resend_invitations`, {
    invite_ids: ids
  });
}
function getJobs(companySlug) {
  return request.get(`/companies/${companySlug}/jobs?type=active`);
}
function inviteTeammate(companySlug, invites) {
  return request.post(`/companies/${companySlug}/preferences/hiring_teams`, {
    hiring_teams: {
      invites
    }
  });
}

export const HiringTeams = {
  resendInvitations,
  getTeamMembers,
  activateStatus,
  deactivateStatus,
  cancelInvites,
  changeRole,
  inviteTeammate,
  getJobs
};

// CareerPage entity
const getCareerPageData = companySlug => request.get(`/companies/${companySlug}/preferences/career_pages`);
const changePartnerListing = ({
  companySlug,
  partnerCompanyId,
  allowListing
}: {
  companySlug: string | number;
  partnerCompanyId: string | number;
  allowListing: boolean;
}) =>
  request.put(`/companies/${companySlug}/preferences/partners/${partnerCompanyId}/change_listing`, {
    allow_listing: allowListing
  });

export const CareerPage = {
  getCareerPageData,
  changePartnerListing
};

// Tags page entity
function getTagsBase(qs = ''): string {
  return `/companies/companyId/preferences/tags` + qs;
}

function getTegWithId(tagId) {
  return `${getTagsBase()}/${tagId}`;
}

export const TagsPage = {
  getTagsBase,
  getTegWithId
};

export const getFilters = () => '/companies/companyId/dashboard/filters';
export const getDashboardJob = id => `/companies/companyId/dashboard/jobs/${id}`;
export const getDashboardJobs = qs => `/companies/companyId/dashboard${qs}`;
export const setJobState = id => `/companies/companyId/jobs/${id}/set_state`;
export const docsURL = (id = null) => '/companies/companyId/attachments' + (id ? `/${id}` : '');
